import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslationChange } from 'i18nano'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { uploadLotPhoto, useAdminTrpc } from 'shared/api'
import type { AdminOutputs } from 'shared/api'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Field, FieldDescription, FieldLabel } from 'shared/components/ui/field'
import { Input } from 'shared/components/ui/input'
import { MultiImageDropzone } from 'shared/components/ui/multi-image-dropzone'
import { Textarea } from 'shared/components/ui/textarea'
import { getLocalizedCategoryName } from 'shared/lib/categories'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import { expandSizes } from '../../admin-create-lot/lib/expand-sizes'
import { buildSlotOperations } from '../lib/slot-operations'
import { buildStockOperations } from '../lib/stock-operations'

type LotDetailOutput = AdminOutputs['lots']['GetById']
type LotPhoto = { key: string; url: string }

type EditableFreeSlot = {
	id: string
	originalName: string
	name: string
}

type NewSlot = {
	tempId: string
	name: string
}

type EditableStockRow = {
	id: string
	originalName: string
	name: string
	maxQuantity: string
	soldQuantity: number
}

type NewStockRow = {
	tempId: string
	name: string
	maxQuantity: string
}

function makeTempId() {
	return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

interface EditLotFormProps {
	lot: LotDetailOutput
}

function EditLotForm({ lot }: EditLotFormProps) {
	const trpc = useAdminTrpc()
	const navigate = useNavigate()
	const { t, prefixT } = useTranslate('pages.adminEditLot')
	const { lang } = useTranslationChange()

	const { data: categories } = useQuery(trpc.categories.List.queryOptions())
	const update = useMutation(trpc.lots.Update.mutationOptions())

	// inventory.type is immutable, so a mounted form is either slots or stock.
	const slotItems = lot.inventory.type === 'slots' ? lot.inventory.items : []
	const stockOriginals = lot.inventory.type === 'stock' ? lot.inventory.items : []

	const [title, setTitle] = useState(lot.title)
	const [photos, setPhotos] = useState<LotPhoto[]>(() =>
		lot.photoKeys.map((key, index) => ({ key, url: lot.photoUrls[index]! })),
	)
	const [uploadingCount, setUploadingCount] = useState(0)
	const [categoryIds, setCategoryIds] = useState<string[]>(lot.categoryIds ?? [])
	const [notes, setNotes] = useState(lot.notes ?? '')
	const [description, setDescription] = useState(lot.description ?? '')
	const [storeUrl, setStoreUrl] = useState(lot.storeUrl ?? '')
	const [priceUsd, setPriceUsd] = useState(String(lot.priceUsd))
	const [priceUzs, setPriceUzs] = useState(
		lot.priceUzs ? String(lot.priceUzs) : '',
	)

	// Slot management (used when inventory.type === 'slots')
	const [freeSlots, setFreeSlots] = useState<EditableFreeSlot[]>(() =>
		slotItems
			.filter((s) => s.status === 'free')
			.map((s) => ({
				id: s.id,
				originalName: s.name,
				name: s.name,
			})),
	)
	const [removedSlotIds, setRemovedSlotIds] = useState<string[]>([])
	const [newSlots, setNewSlots] = useState<NewSlot[]>([])
	const [newSlotInput, setNewSlotInput] = useState('')

	// Stock management (used when inventory.type === 'stock')
	const [stockRows, setStockRows] = useState<EditableStockRow[]>(() =>
		stockOriginals.map((i) => ({
			id: i.id,
			originalName: i.name,
			name: i.name,
			maxQuantity: String(i.maxQuantity),
			soldQuantity: i.soldQuantity,
		})),
	)
	const [removedItemIds, setRemovedItemIds] = useState<string[]>([])
	const [newStockRows, setNewStockRows] = useState<NewStockRow[]>([])

	function toggleCategory(catId: string) {
		setCategoryIds((ids) =>
			ids.includes(catId) ? ids.filter((x) => x !== catId) : [...ids, catId],
		)
	}

	function handlePhotos(files: File[]) {
		setUploadingCount((n) => n + files.length)
		for (const file of files) {
			void uploadLotPhoto(file)
				.then((photo) => setPhotos((current) => [...current, photo]))
				.catch(() => toast.error(prefixT('photoUploadFailed')))
				.finally(() => setUploadingCount((n) => n - 1))
		}
	}

	const paidSlots = useMemo(
		() =>
			lot.inventory.type === 'slots'
				? lot.inventory.items.filter((s) => s.status === 'paid')
				: [],
		[lot.inventory],
	)

	// Price edits are locked once any inventory has sold, in either mode.
	const anyInventorySold =
		lot.inventory.type === 'slots'
			? paidSlots.length > 0
			: stockOriginals.some((i) => i.soldQuantity > 0)

	const usd = Number(priceUsd)
	const { data: calc } = useQuery({
		...trpc.lots.Calculate.queryOptions({ priceUsd: usd }),
		enabled: usd > 0 && !anyInventorySold,
	})

	useEffect(() => {
		// Sync the UZS field to the freshly recalculated recommended price when the
		// admin edits the USD price; reacting to the async calc result is the point.
		if (priceUsd !== String(lot.priceUsd) && calc?.priceUzs !== undefined) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setPriceUzs(String(calc.priceUzs))
		}
	}, [calc?.priceUzs, priceUsd, lot.priceUsd])

	// --- Slot handlers ---
	function handleAddSlots() {
		const expanded = expandSizes(newSlotInput)
		if (expanded.length === 0) return
		setNewSlots((prev) => [
			...prev,
			...expanded.map((name) => ({ tempId: makeTempId(), name })),
		])
		setNewSlotInput('')
	}

	function handleRemoveFreeSlot(slotId: string) {
		setFreeSlots((prev) => prev.filter((s) => s.id !== slotId))
		setRemovedSlotIds((prev) => [...prev, slotId])
	}

	function handleRemoveNewSlot(tempId: string) {
		setNewSlots((prev) => prev.filter((s) => s.tempId !== tempId))
	}

	function handleUpdateFreeSlotName(slotId: string, name: string) {
		setFreeSlots((prev) =>
			prev.map((s) => (s.id === slotId ? { ...s, name } : s)),
		)
	}

	// --- Stock handlers ---
	function updateStockRow(id: string, patch: Partial<Omit<EditableStockRow, 'id'>>) {
		setStockRows((prev) =>
			prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
		)
	}

	function handleRemoveStockRow(id: string) {
		setStockRows((prev) => prev.filter((row) => row.id !== id))
		setRemovedItemIds((prev) => [...prev, id])
	}

	function handleAddStockRow() {
		setNewStockRows((prev) => [
			...prev,
			{ tempId: makeTempId(), name: '', maxQuantity: '' },
		])
	}

	function updateNewStockRow(tempId: string, patch: Partial<Omit<NewStockRow, 'tempId'>>) {
		setNewStockRows((prev) =>
			prev.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
		)
	}

	function handleRemoveNewStockRow(tempId: string) {
		setNewStockRows((prev) => prev.filter((row) => row.tempId !== tempId))
	}

	// --- Validity ---
	const totalRemainingSlotsCount =
		paidSlots.length + freeSlots.length + newSlots.length
	const hasEmptyFreeSlotName =
		freeSlots.some((s) => !s.name.trim()) || newSlots.some((s) => !s.name.trim())

	const totalRemainingStockCount = stockRows.length + newStockRows.length
	const hasInvalidStockRow =
		stockRows.some((row) => {
			const name = row.name.trim()
			const qty = Number(row.maxQuantity)
			if (!name) return true
			if (!Number.isInteger(qty) || qty < 1) return true
			// maxQuantity can never drop below what is already sold.
			return qty < row.soldQuantity
		}) ||
		newStockRows.some((row) => {
			const name = row.name.trim()
			const qty = Number(row.maxQuantity)
			return !name || !Number.isInteger(qty) || qty < 1
		})

	const inventoryInvalid =
		lot.inventory.type === 'slots'
			? totalRemainingSlotsCount === 0 || hasEmptyFreeSlotName
			: totalRemainingStockCount === 0 || hasInvalidStockRow

	async function handleSave() {
		const inventoryOps =
			lot.inventory.type === 'slots'
				? {
						slotOperations: buildSlotOperations({
							originalFreeSlots: slotItems
								.filter((s) => s.status === 'free')
								.map((s) => ({ id: s.id, name: s.name })),
							currentFreeSlots: freeSlots,
							removedSlotIds,
							newSlots,
						}),
					}
				: {
						stockOperations: buildStockOperations({
							originalItems: stockOriginals.map((i) => ({
								id: i.id,
								name: i.name,
								maxQuantity: i.maxQuantity,
								soldQuantity: i.soldQuantity,
							})),
							currentItems: stockRows.map((row) => ({
								id: row.id,
								name: row.name,
								maxQuantity: Number(row.maxQuantity),
							})),
							removedItemIds,
							newItems: newStockRows.map((row) => ({
								name: row.name,
								maxQuantity: Number(row.maxQuantity),
							})),
						}),
					}

		try {
			await update.mutateAsync({
				id: lot._id,
				title: title.trim(),
				photoKeys: photos.map((photo) => photo.key),
				categoryIds,
				notes: notes.trim() || undefined,
				description: description.trim() || undefined,
				storeUrl: storeUrl.trim() || undefined,
				...(!anyInventorySold
					? {
							priceUsd: usd > 0 ? usd : undefined,
							priceUzs: priceUzs.trim() ? Number(priceUzs) : undefined,
						}
					: {}),
				...inventoryOps,
			})
			toast.success(prefixT('saved'))
			navigate(`/admin/lots/${lot._id}`)
		} catch (error) {
			const msg = error instanceof Error ? error.message : ''
			if (msg.includes('concurrently') || msg.includes('CONFLICT')) {
				toast.error(prefixT('conflictError'))
			} else {
				toast.error(msg || prefixT('saveFailed'))
			}
		}
	}

	return (
		<div className='space-y-4 p-4'>
			<div className='flex items-center gap-2'>
				<Button
					variant='ghost'
					size='icon'
					onClick={() => navigate(`/admin/lots/${lot._id}`)}
				>
					<ArrowLeft className='size-5' />
				</Button>
				<h1 className='flex-1 truncate text-xl font-semibold text-foreground'>
					{prefixT('title')}
				</h1>
			</div>

			<Field>
				<FieldLabel htmlFor='title'>{prefixT('name')}</FieldLabel>
				<Input
					id='title'
					value={title}
					onChange={(e) => setTitle(e.target.value)}
				/>
			</Field>

			<Field>
				<FieldLabel>{prefixT('productPhotos')}</FieldLabel>
				<MultiImageDropzone
					urls={photos.map((photo) => photo.url)}
					onFiles={handlePhotos}
					onRemove={(i) =>
						setPhotos((current) => current.filter((_, idx) => idx !== i))
					}
					uploadingCount={uploadingCount}
				/>
			</Field>

			<Field>
				<FieldLabel>{prefixT('categories')}</FieldLabel>
				{!categories?.length ? (
					<FieldDescription>{prefixT('noCategories')}</FieldDescription>
				) : (
					<div className='flex flex-wrap gap-2'>
						{categories.map((cat) => (
							<Button
								key={cat._id}
								type='button'
								size='sm'
								variant={categoryIds.includes(cat._id) ? 'default' : 'outline'}
								onClick={() => toggleCategory(cat._id)}
							>
								{getLocalizedCategoryName(cat, lang)}
							</Button>
						))}
					</div>
				)}
			</Field>

			<Field>
				<FieldLabel htmlFor='notes'>{prefixT('notes')}</FieldLabel>
				<Textarea
					id='notes'
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
				/>
			</Field>

			<Field>
				<FieldLabel htmlFor='description'>{prefixT('buyerDescription')}</FieldLabel>
				<Textarea
					id='description'
					value={description}
					onChange={(e) => setDescription(e.target.value)}
				/>
			</Field>

			<Field>
				<FieldLabel htmlFor='storeUrl'>{prefixT('storeLink')}</FieldLabel>
				<Input
					id='storeUrl'
					type='url'
					placeholder='https://...'
					value={storeUrl}
					onChange={(e) => setStoreUrl(e.target.value)}
				/>
			</Field>

			<Field>
				<FieldLabel htmlFor='usd'>{prefixT('wholesalePrice')}</FieldLabel>
				<Input
					id='usd'
					type='number'
					inputMode='decimal'
					disabled={anyInventorySold}
					value={priceUsd}
					onChange={(e) => setPriceUsd(e.target.value)}
				/>
				{anyInventorySold ? (
					<FieldDescription className='text-amber-600 dark:text-amber-400'>
						{prefixT('priceLocked')}
					</FieldDescription>
				) : (
					calc && (
						<FieldDescription>
							{prefixT('recommendedPrice', {
								price: t('common.amountUzs', {
									amount: formatUzsAmount(calc.priceUzs),
								}),
								rate: String(calc.rate),
								commission: String(calc.commissionPercent),
							})}
						</FieldDescription>
					)
				)}
			</Field>

			<Field>
				<FieldLabel htmlFor='uzs'>{prefixT('priceUzs')}</FieldLabel>
				<Input
					id='uzs'
					type='number'
					inputMode='numeric'
					disabled={anyInventorySold}
					placeholder={calc ? String(calc.priceUzs) : ''}
					value={priceUzs}
					onChange={(e) => setPriceUzs(e.target.value)}
				/>
			</Field>

			{lot.inventory.type === 'slots' ? (
				<div className='space-y-3'>
					<FieldLabel>{prefixT('slots')}</FieldLabel>

					{paidSlots.length > 0 && (
						<div className='space-y-2'>
							<span className='text-xs font-medium text-muted-foreground'>
								{prefixT('paidSlots')}
							</span>
							<div className='divide-y rounded-lg border bg-muted/30'>
								{paidSlots.map((s) => (
									<div
										key={s.id}
										className='flex items-center justify-between p-2.5 text-sm'
									>
										<div className='flex items-center gap-2'>
											<span className='font-medium'>{s.name}</span>
											<Badge variant='default'>{t('status.slot.paid')}</Badge>
										</div>
										<span className='text-xs text-muted-foreground'>
											{s.buyerTezId ??
												s.buyerName ??
												(s.buyerTelegramId ? `id${s.buyerTelegramId}` : '—')}
										</span>
									</div>
								))}
							</div>
						</div>
					)}

					<div className='space-y-2'>
						<span className='text-xs font-medium text-muted-foreground'>
							{prefixT('freeSlots')}
						</span>
						{freeSlots.length === 0 && newSlots.length === 0 ? (
							<div className='rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground'>
								{prefixT('noSlots')}
							</div>
						) : (
							<div className='space-y-2'>
								{freeSlots.map((s) => (
									<div key={s.id} className='flex items-center gap-2'>
										<Input
											value={s.name}
											onChange={(e) =>
												handleUpdateFreeSlotName(s.id, e.target.value)
											}
											className='flex-1'
											placeholder='36'
										/>
										<Badge variant='secondary'>{t('status.slot.free')}</Badge>
										<Button
											type='button'
											variant='ghost'
											size='icon'
											className='text-destructive hover:bg-destructive/10'
											onClick={() => handleRemoveFreeSlot(s.id)}
											title={prefixT('deleteSlot')}
										>
											<Trash2 className='size-4' />
										</Button>
									</div>
								))}
								{newSlots.map((s) => (
									<div key={s.tempId} className='flex items-center gap-2'>
										<Input
											value={s.name}
											onChange={(e) =>
												setNewSlots((prev) =>
													prev.map((item) =>
														item.tempId === s.tempId
															? { ...item, name: e.target.value }
															: item,
													),
												)
											}
											className='flex-1 border-primary/50'
											placeholder='36'
										/>
										<Badge variant='outline'>{t('status.slot.free')}</Badge>
										<Button
											type='button'
											variant='ghost'
											size='icon'
											className='text-destructive hover:bg-destructive/10'
											onClick={() => handleRemoveNewSlot(s.tempId)}
											title={prefixT('deleteSlot')}
										>
											<Trash2 className='size-4' />
										</Button>
									</div>
								))}
							</div>
						)}
					</div>

					<div className='rounded-lg border p-3 space-y-2 bg-muted/20'>
						<FieldLabel htmlFor='add-slot-input'>{prefixT('addSlot')}</FieldLabel>
						<div className='flex gap-2'>
							<Input
								id='add-slot-input'
								placeholder={prefixT('addSlotPlaceholder')}
								value={newSlotInput}
								onChange={(e) => setNewSlotInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault()
										handleAddSlots()
									}
								}}
							/>
							<Button
								type='button'
								variant='outline'
								onClick={handleAddSlots}
								disabled={!newSlotInput.trim()}
							>
								<Plus className='mr-1 size-4' />
								{prefixT('addSlotButton')}
							</Button>
						</div>
						<FieldDescription>{prefixT('addSlotDescription')}</FieldDescription>
					</div>
				</div>
			) : (
				<div className='space-y-3'>
					<FieldLabel>{prefixT('stockVariants')}</FieldLabel>

					{stockRows.length === 0 && newStockRows.length === 0 ? (
						<div className='rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground'>
							{prefixT('noStockVariants')}
						</div>
					) : (
						<div className='space-y-2'>
							{stockRows.map((row) => {
								const locked = row.soldQuantity > 0
								return (
									<div key={row.id} className='flex items-center gap-2'>
										<Input
											value={row.name}
											onChange={(e) => updateStockRow(row.id, { name: e.target.value })}
											disabled={locked}
											className='flex-1'
											placeholder={prefixT('stockNamePlaceholder')}
										/>
										<Input
											type='number'
											inputMode='numeric'
											min={Math.max(1, row.soldQuantity)}
											value={row.maxQuantity}
											onChange={(e) =>
												updateStockRow(row.id, { maxQuantity: e.target.value })
											}
											className='w-24'
											placeholder={prefixT('maxQuantity')}
										/>
										{locked ? (
											<Badge variant='secondary' className='whitespace-nowrap'>
												{prefixT('sold', { count: String(row.soldQuantity) })}
											</Badge>
										) : (
											<Button
												type='button'
												variant='ghost'
												size='icon'
												className='text-destructive hover:bg-destructive/10'
												onClick={() => handleRemoveStockRow(row.id)}
												title={prefixT('deleteVariant')}
											>
												<Trash2 className='size-4' />
											</Button>
										)}
									</div>
								)
							})}
							{newStockRows.map((row) => (
								<div key={row.tempId} className='flex items-center gap-2'>
									<Input
										value={row.name}
										onChange={(e) =>
											updateNewStockRow(row.tempId, { name: e.target.value })
										}
										className='flex-1 border-primary/50'
										placeholder={prefixT('stockNamePlaceholder')}
									/>
									<Input
										type='number'
										inputMode='numeric'
										min={1}
										value={row.maxQuantity}
										onChange={(e) =>
											updateNewStockRow(row.tempId, { maxQuantity: e.target.value })
										}
										className='w-24 border-primary/50'
										placeholder={prefixT('maxQuantity')}
									/>
									<Button
										type='button'
										variant='ghost'
										size='icon'
										className='text-destructive hover:bg-destructive/10'
										onClick={() => handleRemoveNewStockRow(row.tempId)}
										title={prefixT('deleteVariant')}
									>
										<Trash2 className='size-4' />
									</Button>
								</div>
							))}
						</div>
					)}

					<Button
						type='button'
						variant='outline'
						size='sm'
						onClick={handleAddStockRow}
					>
						<Plus className='mr-1 size-4' />
						{prefixT('addStockVariant')}
					</Button>
					<FieldDescription>{prefixT('stockVariantsDescription')}</FieldDescription>
				</div>
			)}

			<Button
				onClick={handleSave}
				disabled={
					update.isPending ||
					uploadingCount > 0 ||
					!title.trim() ||
					photos.length === 0 ||
					inventoryInvalid ||
					(!anyInventorySold && usd <= 0)
				}
				className='w-full'
			>
				{update.isPending ? prefixT('saving') : prefixT('save')}
			</Button>
		</div>
	)
}

export function EditLot() {
	const { id } = useParams<{ id: string }>()
	const trpc = useAdminTrpc()
	const navigate = useNavigate()
	const { t, prefixT } = useTranslate('pages.adminEditLot')

	const { data: lot, isLoading, isError } = useQuery(
		trpc.lots.GetById.queryOptions({ id: id! }),
	)

	if (isLoading) {
		return <div className='p-4 text-foreground'>{t('common.loading')}</div>
	}

	if (isError || !lot) {
		return (
			<div className='space-y-4 p-4'>
				<p className='text-sm text-destructive'>{prefixT('saveFailed')}</p>
				<Button variant='outline' onClick={() => navigate('/admin/lots')}>
					<ArrowLeft className='mr-2 size-4' />
					{t('common.back')}
				</Button>
			</div>
		)
	}

	if (
		lot.state !== 'draft' &&
		lot.state !== 'collecting' &&
		lot.state !== 'paused'
	) {
		return (
			<div className='space-y-4 p-4'>
				<div className='rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive'>
					{prefixT('notEditable')}
				</div>
				<Button variant='outline' onClick={() => navigate(`/admin/lots/${lot._id}`)}>
					<ArrowLeft className='mr-2 size-4' />
					{t('common.back')}
				</Button>
			</div>
		)
	}

	return <EditLotForm key={lot._id} lot={lot} />
}
